/**
 * ============================================================
 * Style Playground — Auto-generated metadata
 * ============================================================
 * Device: desktop
 * Language: English
 *
 * ## Style Direction (from analysis)
 * <style-name>PristineBento</style-name>
 * **Mode:** Light
 * 
 * **Style Recipe:**
 * - **Core Visual Style:** Flat 2.0 / Soft Shadow
 * - **Layout Structure:** Bento Box / Modular
 * - **Visual Complexity:** Balanced
 * 
 * **Implementation Techniques:**
 * - **Material & Surfaces:** Crisp white cards resting on a very subtle off-white or cool-gray background (`#F7F9FC`).
 * - **Border-Radius:** 12px to 16px for main layout containers (Bento cards); 6px to 8px for internal interactive elements (buttons, inputs).
 * - **Typography:** A highly legible, geometric sans-serif (e.g., Inter, Plus Jakarta Sans). High contrast for primary text, specific monospace fonts (like JetBrains Mono) for code snippets and token values.
 * - **Lighting & Shadows:** Multi-layered, highly diffused drop shadows (e.g., `0 4px 12px rgba(0,0,0,0.03)`) that provide a floating, elevated feel without heavy contrast.
 * - **Border Treatments:** 1px solid borders in extremely light gray (e.g., `rgba(0,0,0,0.05)`) to supplement the shadows and give the bento boxes a crisp edge.
 * 
 * **Color Direction:**
 * A sophisticated Deep Cobalt Blue or Indigo as the primary interactive color, ensuring a professional SaaS feel. The palette relies heavily on stark contrasts—pure whites, deep slate grays for text, and low-saturation semantic colors (soft greens for 'published', muted oranges for 'draft') to keep the focus purely on the user's uploaded component designs.
 * ============================================================
 */

import React, { useState } from 'react';
import { Icon } from '@iconify/react';

export default function ComponentDetailPreview() {
  const [activeTab, setActiveTab] = useState('design');
  const [activeSidebar, setActiveSidebar] = useState('components');

  const tabs = [
    { id: 'design', label: 'Design', icon: 'lucide:pen-tool' },
    { id: 'code', label: 'Code', icon: 'lucide:code' },
    { id: 'props', label: 'Props & API', icon: 'lucide:settings-2' },
    { id: 'guidelines', label: 'Guidelines', icon: 'lucide:file-text' },
  ];

  const propsData = [
    { name: 'variant', type: "'primary' | 'secondary' | 'danger'", default: "'primary'", desc: 'The visual style of the button' },
    { name: 'size', type: "'sm' | 'md' | 'lg'", default: "'md'", desc: 'The size of the button' },
    { name: 'disabled', type: 'boolean', default: 'false', desc: 'Whether the button is interactive' },
    { name: 'onClick', type: '(event: React.MouseEvent) => void', default: 'undefined', desc: 'Optional click handler' },
  ];

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900 font-sans selection:bg-indigo-100 selection:text-indigo-900 overflow-hidden">
      
      {/* Sidebar (Navigation blends into background) */}
      <aside className="w-64 flex flex-col bg-slate-50 border-r border-slate-200/60 z-10">
        <div className="h-16 flex items-center px-6 border-b border-slate-200/60 mb-4">
          <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center text-white mr-3 shadow-sm shadow-indigo-600/20">
            <Icon icon="lucide:layers" className="w-5 h-5" />
          </div>
          <span className="font-semibold text-slate-800 tracking-tight">ComponentHub</span>
        </div>

        <nav className="flex-1 px-3 space-y-1">
          {[
            { id: 'dashboard', icon: 'lucide:layout-dashboard', label: 'Dashboard' },
            { id: 'components', icon: 'lucide:component', label: 'Components' },
            { id: 'tokens', icon: 'lucide:palette', label: 'Design Tokens' },
            { id: 'changelog', icon: 'lucide:history', label: 'Changelog' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveSidebar(item.id)}
              className={`w-full flex items-center px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                activeSidebar === item.id
                  ? 'bg-white text-indigo-700 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200/50'
                  : 'text-slate-600 hover:bg-slate-100/80 hover:text-slate-900 border border-transparent'
              }`}
            >
              <Icon icon={item.icon} className={`w-4 h-4 mr-3 ${activeSidebar === item.id ? 'text-indigo-600' : 'text-slate-400'}`} />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-200/60">
          <button className="flex items-center w-full px-2 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
            <img src="https://i.pravatar.cc/100?img=32" alt="User" className="w-8 h-8 rounded-full border border-slate-200 mr-3" />
            <span className="flex-1 text-left truncate">Sarah Designer</span>
            <Icon icon="lucide:chevrons-up-down" className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      </aside>

      {/* Main Content (Bento Box / Modular Layout) */}
      <main className="flex-1 flex flex-col min-w-0 bg-[#F7F9FC]">
        
        {/* Header */}
        <header className="h-20 flex items-center justify-between px-8 bg-white/50 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-10">
          <div className="flex items-center space-x-4">
            <button className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors">
              <Icon icon="lucide:arrow-left" className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Primary Button</h1>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200/60">
                  Published
                </span>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                  v1.2.0
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">Core interaction element for primary actions.</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button className="flex items-center px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg shadow-sm hover:bg-slate-50 transition-all">
              <Icon icon="lucide:history" className="w-4 h-4 mr-2 text-slate-400" />
              Versions
            </button>
            <button className="flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm shadow-indigo-600/20 hover:bg-indigo-700 transition-all">
              <Icon icon="lucide:play" className="w-4 h-4 mr-2" />
              Preview
            </button>
            <button className="p-2 text-slate-400 hover:text-slate-700 bg-white border border-slate-200 rounded-lg shadow-sm hover:bg-slate-50 transition-all">
              <Icon icon="lucide:more-vertical" className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Content Area */}
        <div className="flex-1 overflow-auto p-8">
          
          {/* Tabs Container (Bento styling) */}
          <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 mb-6 flex p-1.5 w-fit">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-slate-50 text-indigo-700 shadow-sm border border-slate-200/50'
                    : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50/50 border border-transparent'
                }`}
              >
                <Icon icon={tab.icon} className={`w-4 h-4 mr-2 ${activeTab === tab.id ? 'text-indigo-600' : 'text-slate-400'}`} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Main Content Bento Box */}
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-slate-100 min-h-[500px] overflow-hidden flex flex-col">
            
            {activeTab === 'design' && (
              <div className="flex-1 flex flex-col">
                <div className="flex justify-between items-center p-5 border-b border-slate-100 bg-slate-50/50">
                  <h3 className="text-sm font-semibold text-slate-800">Figma Prototype</h3>
                  <button className="text-xs font-medium text-indigo-600 hover:text-indigo-700 bg-indigo-50 px-3 py-1.5 rounded-lg flex items-center transition-colors">
                    <Icon icon="lucide:external-link" className="w-3.5 h-3.5 mr-1.5" />
                    Open in Figma
                  </button>
                </div>
                <div className="flex-1 bg-slate-100/50 p-8 flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] [background-size:16px_16px] opacity-50"></div>
                  
                  {/* Mock Figma Embed Content */}
                  <div className="relative bg-white p-12 rounded-2xl shadow-xl border border-slate-200 flex flex-col items-center gap-8 z-10">
                    <div className="flex gap-6 items-end">
                      <div className="flex flex-col items-center gap-3">
                        <span className="text-xs text-slate-400 font-medium tracking-wider uppercase">Large</span>
                        <button className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-medium shadow-sm hover:bg-indigo-700 transition-colors">Primary Action</button>
                      </div>
                      <div className="flex flex-col items-center gap-3">
                        <span className="text-xs text-slate-400 font-medium tracking-wider uppercase">Medium</span>
                        <button className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-medium shadow-sm hover:bg-indigo-700 transition-colors">Primary Action</button>
                      </div>
                      <div className="flex flex-col items-center gap-3">
                        <span className="text-xs text-slate-400 font-medium tracking-wider uppercase">Small</span>
                        <button className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium shadow-sm hover:bg-indigo-700 transition-colors">Primary Action</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'props' && (
              <div className="flex-1 flex flex-col p-8">
                <div className="mb-6 flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Properties & API</h2>
                    <p className="text-sm text-slate-500 mt-1">Configure the button component using these props.</p>
                  </div>
                </div>
                
                <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="py-3 px-4 text-xs font-semibold text-slate-600 tracking-wider uppercase">Name</th>
                        <th className="py-3 px-4 text-xs font-semibold text-slate-600 tracking-wider uppercase">Type</th>
                        <th className="py-3 px-4 text-xs font-semibold text-slate-600 tracking-wider uppercase">Default</th>
                        <th className="py-3 px-4 text-xs font-semibold text-slate-600 tracking-wider uppercase">Description</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {propsData.map((prop, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                          <td className="py-4 px-4 whitespace-nowrap">
                            <code className="text-sm font-mono text-indigo-600 bg-indigo-50 px-2 py-1 rounded-md border border-indigo-100/50">{prop.name}</code>
                          </td>
                          <td className="py-4 px-4 whitespace-nowrap">
                            <code className="text-xs font-mono text-slate-600 bg-slate-100 px-2 py-1 rounded-md border border-slate-200/50">{prop.type}</code>
                          </td>
                          <td className="py-4 px-4 whitespace-nowrap">
                            <code className="text-xs font-mono text-slate-500">{prop.default}</code>
                          </td>
                          <td className="py-4 px-4 text-sm text-slate-600">{prop.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {(activeTab === 'code' || activeTab === 'guidelines') && (
              <div className="flex-1 flex items-center justify-center p-8 text-slate-400 bg-slate-50/30">
                <div className="text-center">
                  <Icon icon="lucide:construction" className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>Content for {activeTab} tab would render here.</p>
                </div>
              </div>
            )}
          </div>

        </div>
      </main>
    </div>
  );
}
